/**
 * Google Translate mutates the DOM by wrapping text nodes with additional spans.
 * When React later tries to clean up its tree, the expected child nodes are no
 * longer present which throws `NotFoundError: removeChild`. This patch wraps
 * `Node.prototype.removeChild` so we safely ignore that specific error.
 */
const patchGoogleTranslateDom = () => {
  if (typeof window === "undefined" || typeof Node === "undefined") {
    return;
  }

  const proto = Node.prototype as Node & {
    __gtTranslatePatched__?: boolean;
  };

  if (proto.__gtTranslatePatched__) {
    return;
  }

  const originalRemoveChild = proto.removeChild;

  proto.removeChild = function <T extends Node>(child: T): T {
    if (!child) {
      return child;
    }

    if (child.parentNode !== this) {
      return child;
    }

    try {
      return originalRemoveChild.call(this, child) as T;
    } catch (error) {
      if (
        error instanceof DOMException &&
        (error.name === "NotFoundError" ||
          error.code === DOMException.NOT_FOUND_ERR)
      ) {
        return child as T;
      }

      throw error;
    }
  };

  proto.__gtTranslatePatched__ = true;
};

patchGoogleTranslateDom();

/**
 * Fire a custom event when SPA navigation happens, so we can avoid polling.
 */
const patchHistoryForLocationChange = () => {
  if (typeof window === "undefined") {
    return;
  }

  const historyRef = window.history as History & {
    __gtHistoryPatched__?: boolean;
  };

  if (historyRef.__gtHistoryPatched__) {
    return;
  }

  const dispatchLocationChange = () => {
    window.dispatchEvent(new Event("locationchange"));
  };

  const originalPushState = historyRef.pushState;
  historyRef.pushState = function (
    ...args: Parameters<History["pushState"]>
  ) {
    const result = originalPushState.apply(this, args);
    dispatchLocationChange();
    return result;
  };

  const originalReplaceState = historyRef.replaceState;
  historyRef.replaceState = function (
    ...args: Parameters<History["replaceState"]>
  ) {
    const result = originalReplaceState.apply(this, args);
    dispatchLocationChange();
    return result;
  };

  window.addEventListener("popstate", dispatchLocationChange);
  historyRef.__gtHistoryPatched__ = true;
};

patchHistoryForLocationChange();

/**
 * Simple fix: Remove aria-hidden from root in admin routes
 * Google Translate sets aria-hidden globally, but we don't use it in admin panel
 */
const fixAriaHiddenForAdmin = () => {
  if (typeof window === "undefined") {
    return;
  }

  const rootElement = document.getElementById("root");
  if (!rootElement) return;

  const isAdminRoute = () => {
    return window.location.pathname.startsWith("/admin");
  };

  const removeAriaHidden = () => {
    if (rootElement.getAttribute("aria-hidden") === "true") {
      rootElement.removeAttribute("aria-hidden");
    }
  };

  // Remove immediately if in admin route
  if (isAdminRoute()) {
    removeAriaHidden();
  }

  const handleLocationChange = () => {
    if (isAdminRoute()) {
      removeAriaHidden();
    }
  };

  window.addEventListener("locationchange", handleLocationChange);

  // Monitor for aria-hidden changes
  const observer = new MutationObserver(() => {
    // Always remove aria-hidden in admin routes
    if (isAdminRoute()) {
      removeAriaHidden();
    }
  });

  // Monitor root for aria-hidden changes
  observer.observe(rootElement, {
    attributes: true,
    attributeFilter: ["aria-hidden"],
  });
};

fixAriaHiddenForAdmin();

export {};
